export type Block =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "takeaway"; title: string; text: string };

export interface BlogPost {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  category: string;
  /** ISO date used for schema + sorting */
  date: string;
  /** Human readable date shown in the UI */
  dateLabel: string;
  readingTime: string;
  excerpt: string;
  intro: string;
  icon: "coins" | "clipboard" | "boxes" | "building" | "shield" | "calendar";
  ctaLabel: string;
  blocks: Block[];
}

/**
 * Inline links inside block text use markdown-style syntax: [label](/path)
 * They are rendered by BlogPostLayout.
 */
export const blogPosts: BlogPost[] = [
  {
    slug: "stress-free-moving-day",
    title: "10 Ways to Make Moving Day Less Stressful",
    metaTitle: "10 Ways to Make Moving Day Less Stressful | OZ MOVE CO.",
    metaDescription:
      "Ten practical tips from Melbourne removalists to keep moving day calm, fast and organised — from reserving parking to packing an essentials bag.",
    category: "Moving Day Tips",
    date: "2026-07-29",
    dateLabel: "29 July 2026",
    readingTime: "5 min read",
    icon: "calendar",
    excerpt:
      "Moving day runs on preparation. These ten practical habits keep the truck moving, the crew productive and your stress levels low.",
    intro:
      "Most of the stress on moving day comes from small things that were never decided in advance — where the truck parks, which boxes go where, who is watching the dog. Sort those out early and the day becomes a straightforward job rather than a scramble.",
    ctaLabel: "Make Your Move Stress Free",
    blocks: [
      { type: "h2", text: "1. Start earlier than you think you need to" },
      {
        type: "p",
        text: "Melbourne traffic is unforgiving after 8am, and lift bookings in apartment buildings are usually locked to a window. An early start gives you a buffer for the things you cannot control.",
      },
      { type: "h2", text: "2. Keep every access path clear" },
      {
        type: "p",
        text: "Hallways, staircases, driveways and the path from the front door to the truck should be free of boxes, bikes, prams and pot plants before the crew arrives. Removalists carry heavy items backwards more often than you would expect, and a clear run is both faster and safer.",
      },
      { type: "h2", text: "3. Reserve parking at both ends" },
      {
        type: "p",
        text: "A truck parked 40 metres away adds time to every single trip. Where you can, block out the space in front of the property the night before, or arrange a permit through your council if you are in an inner-city street.",
      },
      {
        type: "takeaway",
        title: "Quick win",
        text: "Move your own car into the loading spot the night before, then drive it out just before the truck arrives.",
      },
      { type: "h2", text: "4. Label boxes by destination room" },
      {
        type: "p",
        text: "Write the room in the new home, not the room the box came from. Stick a matching sign on each door at the other end and the unload happens without a single question.",
      },
      { type: "h2", text: "5. Pack an essentials bag" },
      {
        type: "ul",
        items: [
          "Phone chargers, keys and wallet",
          "Medication, glasses and toiletries",
          "A change of clothes and towels",
          "Kettle, mugs, tea, coffee and snacks",
          "Toilet paper, hand soap and bin bags",
          "Tools for reassembling beds",
        ],
      },
      { type: "h2", text: "6. Keep children and pets somewhere safe" },
      {
        type: "p",
        text: "Open doors, trolleys and heavy furniture do not mix well with curious toddlers or nervous cats. A friend's place, a day at childcare, or one closed-off room with food and water all work.",
      },
      { type: "h2", text: "7. Photograph electronics before unplugging" },
      {
        type: "p",
        text: "One photo of the back of the TV, modem and computer saves twenty minutes of guesswork later. Bag the cables and tape them to the item they belong to.",
      },
      { type: "h2", text: "8. Dismantle furniture the day before" },
      {
        type: "p",
        text: "Beds, desks and flat-pack shelving take time to pull apart. Doing it the night before means the crew is loading rather than waiting. Keep screws in labelled zip-lock bags taped to the frame.",
      },
      { type: "h2", text: "9. Flag access issues when you book" },
      {
        type: "p",
        text: "Steep driveways, narrow laneways, a fourth-floor walk-up, a piano or a fridge that only fits through one doorway — all of it changes the crew size, truck size and equipment we bring. Mention it early and the day runs to plan. Our [apartment moves](/apartment-moves) team plans lift and dock access as part of the booking.",
      },
      { type: "h2", text: "10. Keep valuables with you" },
      {
        type: "p",
        text: "Passports, jewellery, hard drives and important documents should travel in your own car. It removes any uncertainty and gives you one less thing to think about.",
      },
      {
        type: "takeaway",
        title: "The short version",
        text: "Clear access, reserved parking, labelled boxes and an early start solve about 80% of moving-day stress before it starts.",
      },
      { type: "h2", text: "Stay available, but let the crew work" },
      {
        type: "p",
        text: "The most helpful thing you can do during the move is be contactable for quick decisions. If you would rather hand the whole day over, our [same day moves](/same-day-moves) and [home moves](/home-moves) crews handle everything from wrapping to placement.",
      },
    ],
  },
  {
    slug: "choosing-a-removalist-melbourne",
    title: "How to Choose a Reliable Removalist in Melbourne",
    metaTitle: "How to Choose a Reliable Removalist in Melbourne | OZ MOVE CO.",
    metaDescription:
      "What to check before booking a Melbourne removalist: insurance, written quotes, reviews, equipment, cancellation terms and the red flags worth avoiding.",
    category: "Choosing a Mover",
    date: "2026-07-17",
    dateLabel: "17 July 2026",
    readingTime: "7 min read",
    icon: "shield",
    excerpt:
      "Insurance, written quotes, reviews and cancellation terms — a practical framework for comparing Melbourne moving companies before you book.",
    intro:
      "Most moving complaints trace back to something that was never clarified before the booking. This guide covers what to check, what to ask, and which warning signs are worth walking away from — whoever you end up hiring.",
    ctaLabel: "Request a Quote From OZ MOVE CO.",
    blocks: [
      { type: "h2", text: "Start with insurance" },
      {
        type: "p",
        text: "Ask two questions: what public liability cover do you hold, and is goods in transit cover included? Public liability protects the property — walls, lifts, floors, common areas. Goods in transit relates to your belongings while they are being carried and driven.",
      },
      {
        type: "p",
        text: "A company that cannot answer clearly, or that says \"we are careful so we do not need it\", is telling you something useful.",
      },
      {
        type: "takeaway",
        title: "Ask for it in writing",
        text: "Cover levels should appear on the quote or be confirmed by email. A verbal assurance is difficult to rely on later.",
      },
      { type: "h2", text: "Read reviews properly" },
      {
        type: "ul",
        items: [
          "Look at recent reviews, not just the overall score",
          "Read the three-star reviews — they are usually the most honest",
          "Check whether the company replies to criticism, and how",
          "Be sceptical of dozens of five-star reviews posted in the same week",
          "Look for detail: named suburbs, specific items, real timelines",
        ],
      },
      { type: "h2", text: "Insist on a written quote" },
      {
        type: "p",
        text: "A written quote should state the hourly rate or fixed price, the minimum booking, the call-out or depot-to-depot fee, the crew size, the truck size and what happens if the job runs long. If any of those are missing, the final invoice is a guess.",
      },
      {
        type: "p",
        text: "Our full breakdown of Melbourne pricing is in [how much removalists cost in Melbourne](/blog/removalist-costs-melbourne).",
      },
      { type: "h2", text: "Check the equipment and the truck" },
      {
        type: "p",
        text: "Trolleys, heavy-duty blankets, shrink wrap, straps, tool kits and a tail-lift make a measurable difference to both speed and safety. Ask what truck size is allocated to your job — an undersized truck means a second trip, and a second trip is billable time.",
      },
      { type: "h2", text: "Experience with your type of move" },
      {
        type: "p",
        text: "A three-bedroom house in Craigieburn and a 12th-floor apartment in Southbank are different jobs. Ask how often they do your type of move. Specialist experience matters most for [apartment moves](/apartment-moves), [office relocations](/office-relocations) and [single item moves](/single-item-moves) involving pianos, safes or fragile furniture.",
      },
      { type: "h2", text: "Judge the communication" },
      {
        type: "p",
        text: "How a company communicates before the booking is a fair preview of how they communicate on the day. Fast replies, clear answers and a named contact are all good signs. Vague answers and unreturned calls rarely improve after payment.",
      },
      { type: "h2", text: "Hidden fees to ask about directly" },
      {
        type: "ul",
        items: [
          "Travel or call-out time and how it is calculated",
          "Stair and long-carry charges",
          "Heavy item fees for pianos, safes and treadmills",
          "Fuel levies or tolls",
          "Weekend, public holiday and after-hours rates",
          "Charges for wrapping materials, boxes or blankets",
          "GST — quoted inclusive or exclusive",
        ],
      },
      { type: "h2", text: "Understand the cancellation terms" },
      {
        type: "p",
        text: "Settlement dates move and leases fall through. Ask how much notice is required to reschedule without a fee, and whether a deposit is refundable. Reasonable companies have a clear, fair policy and will tell you what it is.",
      },
      { type: "h2", text: "Ask who is actually turning up" },
      {
        type: "p",
        text: "Some operators subcontract to whoever is available. That is not automatically bad, but you should know whether the crew is trained, insured under the same policy, and accountable to the company you booked.",
      },
      {
        type: "takeaway",
        title: "Red flags",
        text: "No written quote, cash-only, no ABN, no insurance detail, a price far below every other quote, pressure to pay a large deposit immediately, or a refusal to do a walkthrough of the job over the phone.",
      },
      { type: "h2", text: "Questions worth asking before you book" },
      {
        type: "ol",
        items: [
          "What is included in the hourly rate, and when does the clock start and stop?",
          "What is the minimum booking period?",
          "What insurance do you hold, and what does it cover?",
          "How many movers and what truck size are allocated to my job?",
          "What happens if the job takes longer than estimated?",
          "Do you charge extra for stairs, long carries or heavy items?",
          "What is your reschedule and cancellation policy?",
          "Who do I contact on the day if something changes?",
        ],
      },
      { type: "h2", text: "Comparing quotes fairly" },
      {
        type: "p",
        text: "Line the quotes up on the same basis: rate, minimum hours, travel time, crew size and inclusions. The cheapest hourly rate can easily end up the most expensive job if it comes with two movers instead of three and a truck that needs a second run. If you would like a written, itemised quote for a Melbourne move, [request one here](/#quote) or [get in touch](/contact) with any questions first.",
      },
    ],
  },
  {
    slug: "apartment-moving-melbourne",
    title: "Moving Apartments in Melbourne: What You Need to Know",
    metaTitle: "Moving Apartments in Melbourne: What You Need to Know | OZ MOVE CO.",
    metaDescription:
      "Lift bookings, loading docks, strata rules and CBD parking — a practical guide to planning a smooth Melbourne apartment or high-rise move.",
    category: "Apartment Moves",
    date: "2026-07-05",
    dateLabel: "5 July 2026",
    readingTime: "6 min read",
    icon: "building",
    excerpt:
      "Lift bookings, dock access, strata rules and CBD parking. Apartment moves are won or lost on the paperwork you sort out beforehand.",
    intro:
      "An apartment move has a second layer of logistics on top of the move itself: the building. Lifts, docks, approved hours and building managers all have a say in how your day runs, and most of it has to be arranged a week or two in advance.",
    ctaLabel: "Plan Your Apartment Move",
    blocks: [
      { type: "h2", text: "Book the lift first" },
      {
        type: "p",
        text: "Most Melbourne apartment buildings require a booked service lift for moves, usually in two to four hour blocks. Popular slots on Saturdays go weeks ahead. Book the lift before you lock in the removalist, then match the crew to that window.",
      },
      {
        type: "p",
        text: "Ask the building manager whether the lift is padded for you or whether the crew needs to install protection, and confirm the internal dimensions if you have a large sofa or wardrobe.",
      },
      { type: "h2", text: "Talk to the building manager early" },
      {
        type: "ul",
        items: [
          "Approved moving hours (often 8am–4pm on weekdays, restricted on Sundays)",
          "Whether a certificate of currency for insurance is required in advance",
          "Bond or deposit for lift and common area use",
          "Where the crew signs in and collects keys or swipe access",
          "Rules on propping doors, using the passenger lift, or stair access",
        ],
      },
      {
        type: "takeaway",
        title: "Certificate of currency",
        text: "Many buildings will not release the lift without proof of the removalist's public liability cover. Request it as soon as you book so it is not holding up the day.",
      },
      { type: "h2", text: "Understand the loading dock" },
      {
        type: "p",
        text: "Docks have height limits — commonly between 2.8 and 3.5 metres — and that determines the truck we can send. A dock that fits only a small truck means more trips, so the truck size needs to be decided before the day, not on arrival.",
      },
      {
        type: "p",
        text: "If there is no dock, the crew will be carrying from the street. That changes the time estimate and it is better to price it honestly up front.",
      },
      { type: "h2", text: "Parking and CBD traffic" },
      {
        type: "p",
        text: "Clearways, tram lanes, one-way laneways and permit-only streets are the norm through the CBD, Southbank, Docklands, Carlton and St Kilda. Where there is no dock, apply for a temporary parking permit through your council — most require several business days' notice.",
      },
      {
        type: "p",
        text: "Timing helps too. A start before 8am or a mid-morning start after peak avoids the worst of it.",
      },
      { type: "h2", text: "Measure the tight spots" },
      {
        type: "ol",
        items: [
          "Front door and any internal doorways on the path out",
          "Hallway width and the turn out of the lift lobby",
          "Lift internal depth, width and door height",
          "Balcony access, if a hoist or balcony lift is being considered",
          "Stairwell width and landing turns, as a fallback",
        ],
      },
      {
        type: "p",
        text: "If a wardrobe or sofa bed will not fit, it is far cheaper to know a week out than to discover it with a crew standing in the hallway.",
      },
      { type: "h2", text: "Strata and body corporate rules" },
      {
        type: "p",
        text: "Strata by-laws frequently cover moving hours, noise, use of common areas, waste disposal and protection of floors and walls. Breaching them can cost you a bond. Ask for the moving rules in writing when you book the lift.",
      },
      { type: "h2", text: "Protect the common areas" },
      {
        type: "p",
        text: "Floor runners, corner guards and lift padding are standard on our jobs, and most buildings expect them. It protects your bond and keeps the building manager on side.",
      },
      {
        type: "takeaway",
        title: "Plan for lift delays",
        text: "In a high-rise, the lift is the bottleneck. Staging boxes near the lift lobby in advance means each trip goes out full rather than half empty.",
      },
      { type: "h2", text: "Choosing the right truck" },
      {
        type: "p",
        text: "A one-bedroom apartment usually fits a single run in a mid-size truck. Two and three-bedroom apartments often need a larger truck or a second trip, and dock height may rule the larger truck out. We work this out at quoting stage so the estimate matches reality.",
      },
      { type: "h2", text: "Getting help with a high-rise move" },
      {
        type: "p",
        text: "Our [apartment moves](/apartment-moves) service is built around exactly this — lift bookings, dock access, protection of common areas and crews who move through Melbourne towers every week. See our [Melbourne moving services](/melbourne) for coverage across the metro area, or [request a quote](/#quote) with your building details and we will plan the access with you.",
      },
    ],
  },
  {
    slug: "how-to-pack-for-moving",
    title: "How to Pack for a Move Without Wasting Time",
    metaTitle: "How to Pack for a Move Without Wasting Time | OZ MOVE CO.",
    metaDescription:
      "A practical packing guide for Melbourne movers: supplies, labelling, room-by-room order, fragile items, TVs, and how good packing lowers your removalist bill.",
    category: "Packing Tips",
    date: "2026-06-18",
    dateLabel: "18 June 2026",
    readingTime: "7 min read",
    icon: "boxes",
    excerpt:
      "Supplies, labelling, room-by-room order and fragile items — how to pack quickly, protect your belongings and cut billable hours on the day.",
    intro:
      "Packing is the part of a move you control completely, and it has the biggest effect on how long the day takes. Boxes that are sealed, labelled and stacked near the door load in a fraction of the time of a half-packed house.",
    ctaLabel: "Get Help With Your Move",
    blocks: [
      { type: "h2", text: "Buy the right supplies once" },
      {
        type: "ul",
        items: [
          "Small boxes for books, tools and tinned food",
          "Medium boxes for most household items",
          "Large boxes for linen, pillows and light bulky items",
          "Port-a-robe wardrobe boxes for hanging clothes",
          "Packing paper (newsprint) rather than newspaper, which marks",
          "Bubble wrap for glass and ceramics",
          "Quality packing tape and a tape gun",
          "Thick markers and colour stickers for labelling",
        ],
      },
      {
        type: "p",
        text: "Buy more tape and paper than you think you need. Running out mid-pack costs more in time than the extra roll costs in money.",
      },
      { type: "h2", text: "Label like the crew has never seen your house" },
      {
        type: "p",
        text: "Write the destination room and a one-line summary of contents on the top and on at least one side, so it is readable when boxes are stacked. Colour-coding by room speeds up the unload even further.",
      },
      {
        type: "takeaway",
        title: "Mark fragile boxes on all four sides",
        text: "A single \"FRAGILE\" on the lid disappears the moment something is stacked on top of it.",
      },
      { type: "h2", text: "Pack room by room, least used first" },
      {
        type: "ol",
        items: [
          "Storage, garage and spare rooms — start three to four weeks out",
          "Books, decor, artwork and out-of-season clothing",
          "Spare bedrooms and the linen cupboard",
          "Living room and non-essential kitchen items",
          "Wardrobes, bathrooms and everyday kitchen items in the final week",
          "Essentials box last, packed the night before",
        ],
      },
      {
        type: "p",
        text: "Finish one room completely before starting the next. Half-packed rooms scattered across a house are what turn a four-hour job into a six-hour one.",
      },
      { type: "h2", text: "How heavy should a box be?" },
      {
        type: "p",
        text: "As a rule, if you cannot comfortably lift it yourself, it is too heavy. Around 15–20kg is a sensible ceiling. Heavy items belong in small boxes and light bulky items in large boxes — the opposite of what most people do instinctively.",
      },
      {
        type: "p",
        text: "Fill any gap at the top with paper or linen. A box that flexes will collapse when it is stacked.",
      },
      { type: "h2", text: "Protecting fragile items" },
      {
        type: "ul",
        items: [
          "Wrap each piece individually — never nest unwrapped glassware",
          "Stand plates on their edge rather than flat, like records in a crate",
          "Line the base and top of the box with 5cm of crushed paper",
          "Fill every void so nothing can shift in transit",
          "Keep fragile boxes under 15kg and never stack heavy boxes on them",
        ],
      },
      { type: "h2", text: "Packing a TV" },
      {
        type: "p",
        text: "Use the original box if you still have it. If not, wrap the screen in a blanket or foam sheet, then bubble wrap, tape the bracket and screws to the back in a labelled bag, and transport it upright — never flat, which stresses the panel. Photograph the cabling before unplugging.",
      },
      { type: "h2", text: "Kitchenware" },
      {
        type: "p",
        text: "Pots and pans nest with paper between them. Knives go in a taped-shut knife block or wrapped in a tea towel with the blade covered and clearly marked. Defrost and dry the fridge at least 24 hours before the move, and drain the washing machine.",
      },
      { type: "h2", text: "Clothing and wardrobes" },
      {
        type: "p",
        text: "Wardrobe boxes let hanging clothes travel on the rail and go straight into the new wardrobe — worth it for the time saved alone. Folded clothes can stay in drawers if the unit is sturdy, but ask first: many crews prefer drawers emptied for weight and safety.",
      },
      {
        type: "takeaway",
        title: "Suitcases and laundry baskets are free boxes",
        text: "Use what you already own for clothing, shoes and linen before buying extra cartons.",
      },
      { type: "h2", text: "What not to pack" },
      {
        type: "ul",
        items: [
          "Flammables, gas bottles, petrol, paint and aerosols",
          "Bleach, pool chemicals and other corrosives",
          "Perishable food and open liquids",
          "Passports, deeds, jewellery, cash and hard drives — keep these with you",
          "Medication and anything you will need in the first 24 hours",
        ],
      },
      { type: "h2", text: "Build an essentials box" },
      {
        type: "p",
        text: "One clearly marked box or tub that travels in your car: kettle, mugs, tea and coffee, snacks, phone chargers, toiletries, toilet paper, towels, a change of clothes, basic tools, scissors and bin bags. It is the difference between a calm first night and a frustrating one.",
      },
      { type: "h2", text: "Why good packing lowers your bill" },
      {
        type: "p",
        text: "Most Melbourne removalists charge by the hour. Sealed, labelled, uniform boxes stacked in one accessible spot load quickly and stack safely in the truck, which means fewer trips and less time. Half-packed rooms mean the crew is waiting or packing for you at an hourly rate. There is more detail in our [Melbourne removalist cost guide](/blog/removalist-costs-melbourne), and the full timeline is in [the complete moving house checklist](/blog/moving-house-checklist).",
      },
      {
        type: "p",
        text: "If you would rather not pack at all, our [home moves](/home-moves) crews can handle full or partial packing — just mention it when you [request a quote](/#quote).",
      },
    ],
  },
  {
    slug: "moving-house-checklist",
    title: "The Complete Moving House Checklist",
    metaTitle: "The Complete Moving House Checklist | OZ MOVE CO. Melbourne",
    metaDescription:
      "A week-by-week moving house checklist for Melbourne: utilities, address changes, packing, parking, lift bookings, moving day and settling in afterwards.",
    category: "Moving Preparation",
    date: "2026-06-02",
    dateLabel: "2 June 2026",
    readingTime: "8 min read",
    icon: "clipboard",
    excerpt:
      "Everything to do from four weeks out through to the week after you move, laid out so you can work through it one stage at a time.",
    intro:
      "A move is easy to underestimate because the work is spread across dozens of small tasks. This checklist puts them in order so nothing lands on you at the last minute.",
    ctaLabel: "Book Your Move",
    blocks: [
      { type: "h2", text: "Four weeks before" },
      {
        type: "ul",
        items: [
          "Confirm your settlement or lease dates in writing",
          "Get quotes from removalists and book your preferred date",
          "Book the service lift and loading dock if you are in an apartment",
          "Start decluttering room by room — sell, donate or dispose",
          "Book hard rubbish collection or a skip if you need one",
          "Order boxes, tape, paper and wardrobe boxes",
          "Start packing storage areas, the garage and anything out of season",
          "Tell your landlord or agent in writing if you are renting",
          "Book cleaners for the end of lease or settlement if required",
        ],
      },
      {
        type: "takeaway",
        title: "Declutter before you quote",
        text: "Every item you get rid of now is one you do not pay to wrap, carry and unload. Do it before the walkthrough and your quote drops with it.",
      },
      { type: "h2", text: "Two weeks before" },
      {
        type: "ul",
        items: [
          "Arrange electricity, gas, water and internet at the new address",
          "Book the internet connection — this is the one that runs longest",
          "Set up Australia Post mail redirection",
          "Update your address with your bank, insurer, employer and super fund",
          "Update Medicare, Vicroads, the electoral roll and your GP",
          "Transfer or cancel local memberships and subscriptions",
          "Arrange care for pets and children on the day",
          "Confirm parking arrangements or apply for a council permit",
          "Keep packing — living areas and spare rooms",
        ],
      },
      { type: "h2", text: "One week before" },
      {
        type: "ul",
        items: [
          "Reconfirm the removalist, arrival time, crew size and truck size",
          "Confirm the lift booking and building sign-in process",
          "Finish packing everything except daily essentials",
          "Run down the fridge, freezer and pantry",
          "Service or disconnect appliances that need it",
          "Return borrowed items and collect anything lent out",
          "Take photos of your current property's condition",
          "Label boxes with the destination room and print room signs for the new home",
          "Arrange keys, swipe cards and remotes for both properties",
        ],
      },
      { type: "h2", text: "The day before" },
      {
        type: "ol",
        items: [
          "Defrost and dry the fridge and freezer, and drain the washing machine",
          "Dismantle beds and flat-pack furniture, bagging and taping the screws",
          "Photograph the back of the TV, modem and computer before unplugging",
          "Charge phones, power banks and any power tools",
          "Pack your essentials box and put it in your own car",
          "Set aside valuables and documents to travel with you",
          "Park your own car in the loading spot to hold it overnight",
          "Clear hallways, stairs and the path to the street",
          "Confirm the arrival window with your removalist",
        ],
      },
      { type: "h2", text: "Moving day" },
      {
        type: "ul",
        items: [
          "Be there for the crew's arrival and walk them through the job",
          "Point out fragile, heavy and do-not-load items immediately",
          "Keep pets and children in a closed room or off-site",
          "Keep access clear and stay contactable for quick decisions",
          "Do a final sweep of cupboards, the roof cavity, sheds and the letterbox",
          "Check meter readings and photograph them",
          "Turn off appliances, lock windows and secure the property",
          "Hand back keys, remotes and swipe cards",
          "At the new home, put up room signs before the truck arrives",
          "Direct boxes and furniture as they come in — it saves moving them twice",
        ],
      },
      {
        type: "takeaway",
        title: "The final property check",
        text: "Cupboards, the dishwasher, the letterbox, the shed, under the house and behind doors. Almost every forgotten item lives in one of those places.",
      },
      { type: "h2", text: "After the move" },
      {
        type: "ul",
        items: [
          "Assemble beds first so you can stop at any point in the evening",
          "Check that utilities are connected and working",
          "Unpack the kitchen and bathroom before anything else",
          "Test smoke alarms and change locks if you have bought",
          "Inspect furniture and report any damage to your removalist promptly",
          "Update your address on anything you missed",
          "Break down boxes for recycling or resale",
          "Meet the neighbours and find your nearest bins, parking and transport",
        ],
      },
      { type: "h2", text: "Utilities checklist at a glance" },
      {
        type: "ul",
        items: [
          "Electricity — final reading at the old address, connection at the new",
          "Gas — same, and book a technician if a meter needs turning on",
          "Water — notify the retailer; often handled at settlement",
          "Internet and phone — book earliest, as install windows can be weeks",
          "Streaming, insurance and council rates — update the address",
        ],
      },
      { type: "h2", text: "Getting the move itself booked" },
      {
        type: "p",
        text: "Once the checklist above is under way, the booking is the easy part. We handle [home moves](/home-moves), [apartment moves](/apartment-moves) and [office relocations](/office-relocations) across the metro area — see our [Melbourne page](/melbourne) for coverage, read our [packing guide](/blog/how-to-pack-for-moving) next, or [get a free quote](/#quote) with your dates.",
      },
    ],
  },
  {
    slug: "removalist-costs-melbourne",
    title: "How Much Do Removalists Cost in Melbourne?",
    metaTitle: "How Much Do Removalists Cost in Melbourne? | OZ MOVE CO.",
    metaDescription:
      "How Melbourne removalist pricing works: hourly rates, call-out fees, minimum bookings, stairs, travel time and heavy items — plus how to compare quotes fairly.",
    category: "Moving Costs",
    date: "2026-05-15",
    dateLabel: "15 May 2026",
    readingTime: "6 min read",
    icon: "coins",
    excerpt:
      "Hourly rates, call-out fees, minimum bookings and access charges explained — plus a worked example and how to compare quotes without getting caught out.",
    intro:
      "Removalist pricing in Melbourne looks confusing because companies quote on different bases. Once you know what the components are, comparing quotes becomes straightforward and the final invoice stops being a surprise.",
    ctaLabel: "Get a Free Moving Quote",
    blocks: [
      { type: "h2", text: "Hourly rates are the standard in Melbourne" },
      {
        type: "p",
        text: "Most local Melbourne moves are charged hourly, based on the crew size and truck. Two movers and a truck sit at the lower end, three or four movers at the higher end. A larger crew costs more per hour but usually finishes proportionally faster, so the total is often similar — and sometimes lower for a big job.",
      },
      {
        type: "p",
        text: "Interstate and long-distance work is normally quoted as a fixed price based on volume instead.",
      },
      { type: "h2", text: "Call-out and travel time" },
      {
        type: "p",
        text: "Almost every company charges for the time to get to you and back to base — sometimes as a flat call-out fee, sometimes as an extra 30 to 60 minutes on the invoice, sometimes as depot-to-depot billing. It is not a hidden cost when it is disclosed, but it must be on the quote. Two identical hourly rates can differ by well over $100 on this alone.",
      },
      { type: "h2", text: "Minimum booking periods" },
      {
        type: "p",
        text: "Two to three hours is the usual minimum in Melbourne. If your job is a studio apartment that takes 90 minutes, you will still be charged the minimum. Worth knowing before you compare a two-hour minimum against a four-hour one.",
      },
      {
        type: "takeaway",
        title: "Ask when the clock starts",
        text: "On arrival, on departure from the depot, or on the first item loaded? This single question explains most of the difference between quotes.",
      },
      { type: "h2", text: "Stairs, lifts and long carries" },
      {
        type: "ul",
        items: [
          "Stairs slow every trip, so they add time whether or not there is a separate fee",
          "Some companies charge a per-flight surcharge; others simply bill the extra hours",
          "A long carry from the truck to the door — 30 metres or more — has the same effect",
          "Lift-only buildings can be slower than two flights of stairs during peak times",
        ],
      },
      { type: "h2", text: "Heavy and specialty items" },
      {
        type: "p",
        text: "Pianos, safes, pool tables, large fridges, treadmills and marble tables usually attract a specialty fee because they need extra crew and equipment. Declare them at quoting stage — a crew that arrives without the right gear cannot move the item at all. We handle these through our [single item moves](/single-item-moves) service.",
      },
      { type: "h2", text: "Packing services" },
      {
        type: "p",
        text: "Full packing, partial packing and materials are usually charged separately — either hourly or as a package. If you pack yourself, use our [packing guide](/blog/how-to-pack-for-moving) to keep the boxes uniform and the load fast.",
      },
      { type: "h2", text: "A worked example" },
      {
        type: "p",
        text: "Say you are moving a two-bedroom apartment in Brunswick to a house in Coburg, with lift access at one end and a driveway at the other, everything packed and stacked in the lounge.",
      },
      {
        type: "ol",
        items: [
          "Three movers and a truck, quoted at an hourly rate agreed in advance",
          "Loading takes roughly 1.5 hours with a booked lift and clear access",
          "Travel between the two addresses is about 20 minutes",
          "Unloading and placement takes roughly 1.25 hours",
          "Add the disclosed call-out or travel component",
          "Total billed time lands around 3.5 hours plus travel",
        ],
      },
      {
        type: "p",
        text: "The same job with unpacked cupboards, no lift booking and street parking 40 metres away can easily run to five or six hours. The rate did not change — the conditions did. Ask us for a written estimate on your specific job rather than relying on a general figure; we quote each move on its access, volume and distance.",
      },
      { type: "h2", text: "What pushes the price up" },
      {
        type: "ul",
        items: [
          "Packing not finished when the crew arrives",
          "No reserved parking, or a long carry to the truck",
          "Multiple flights of stairs at either end",
          "Unbooked or shared lifts in a busy building",
          "Furniture that still needs dismantling",
          "Extra stops, storage or a second trip because the truck is undersized",
          "Weekend, public holiday and end-of-month peak dates",
        ],
      },
      { type: "h2", text: "What brings it down" },
      {
        type: "ul",
        items: [
          "Declutter first — fewer items is directly less time",
          "Everything boxed, sealed, labelled and stacked in one spot",
          "Beds and flat-packs dismantled the night before",
          "Parking reserved at both addresses",
          "A mid-week, mid-month date where your schedule allows",
          "An accurate inventory at quoting stage so the right truck arrives first time",
        ],
      },
      { type: "h2", text: "How to compare quotes properly" },
      {
        type: "ol",
        items: [
          "Put every quote on the same basis: rate, crew size, truck size, minimum hours",
          "Add the call-out or travel component to each before comparing",
          "Check whether GST is included",
          "Confirm what insurance is included, not just offered as an extra",
          "Check for surcharges on stairs, heavy items and weekends",
          "Ask what happens if the job runs longer than estimated",
        ],
      },
      {
        type: "takeaway",
        title: "Cheapest is not always cheapest",
        text: "A low hourly rate with two movers, a small truck and a four-hour minimum frequently costs more than a higher rate with three movers who finish in half the time — and damage or a second trip erases the saving completely.",
      },
      { type: "h2", text: "Preparing to reduce billable time" },
      {
        type: "p",
        text: "The single biggest lever you control is readiness. Work through [the complete moving house checklist](/blog/moving-house-checklist) in the weeks beforehand, and see [how to choose a reliable removalist](/blog/choosing-a-removalist-melbourne) before you book. When you are ready, [get a free written quote](/#quote) or [contact our team](/contact) to talk through your job.",
      },
    ],
  },
];

export const postsNewestFirst = [...blogPosts].sort((a, b) => (a.date < b.date ? 1 : -1));

export const getPost = (slug: string) => blogPosts.find((p) => p.slug === slug);
